import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Caption,
  DropZone as ShopifyDropZone,
  DropZoneProps as ShopifyDropZoneProps,
  Spinner,
  Stack,
  Thumbnail,
} from '@shopify/polaris';
import { useRecoilState } from 'recoil';
import { notificationErrorState } from '@states';
import { bytesToMegabytes } from '@utils/transform';
import styled from 'styled-components';
import { HelpText } from '../common';

const Container = styled.div<{
  loading?: boolean;
}>`
  ${HelpText} {
    margin-top: 0.7rem;
  }

  .Polaris-DropZone__Container {
    display: ${(props) => (props.loading ? 'flex' : undefined)};
    justify-content: center;
    align-items: center;
    height: ${(props) => (props.loading ? '172px' : undefined)};
  }
`;

interface DropZoneProps extends ShopifyDropZoneProps {
  files: File[];
  loading?: boolean;
}

const DropZone: FunctionComponent<DropZoneProps> = (props) => {
  const { allowMultiple, disabled, files: filesProp, loading, onDrop } = props;

  const [files, setFiles] = useState<File[]>(filesProp);
  const [, setNotificationError] = useRecoilState(notificationErrorState);

  useEffect(() => {
    setFiles(filesProp);
  }, [filesProp]);

  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
      const filesToSave = allowMultiple
        ? ([...files, acceptedFiles] as File[])
        : [acceptedFiles[0]];

      const totalFileSize = filesToSave.reduce(
        (previousValue, currentFile) => previousValue + currentFile.size,
        0,
      );

      if (bytesToMegabytes(totalFileSize) > 10) {
        setNotificationError(
          'Uploaded files exceed max upload size limit of 10MB',
        );
        return;
      }

      setFiles(filesToSave);
      onDrop && onDrop(_dropFiles, filesToSave, _rejectedFiles);
    },
    [allowMultiple, files, onDrop, setNotificationError],
  );

  return (
    <Container loading={loading}>
      <ShopifyDropZone
        {...props}
        onDrop={handleDropZoneDrop}
        variableHeight={true}
        disabled={disabled ?? loading}
      >
        {loading && <Spinner />}
        {!loading && !files.length && <ShopifyDropZone.FileUpload />}
        {!loading &&
          files.length > 0 &&
          files.map((file, index) => (
            <Stack alignment="center" key={index}>
              <Thumbnail
                alt={file.name}
                source={window.URL.createObjectURL(file)}
              />
              <div>
                {file.name} <Caption>{bytesToMegabytes(file.size)} MB</Caption>
              </div>
            </Stack>
          ))}
      </ShopifyDropZone>
      <HelpText>Max file upload size: 10MB</HelpText>
    </Container>
  );
};

export default DropZone;
